import { type ISelectProps, Select, SubText } from '@procurify/ui'
import { useIntl } from 'react-intl'
import {
  components as ReactSelectComponents,
  type OptionProps,
  type SingleValueProps,
} from 'react-select'

import { useFormatTaxPercentage } from '@webapp/hooks/useFormatTaxPercentage'
import { type Tax } from '@webapp/models'
import messages from './messages'

type TaxSelectOptionType = Tax

const components = {
  Option: (props: OptionProps<TaxSelectOptionType, false>) => {
    const intl = useIntl()
    const formatTaxPercentage = useFormatTaxPercentage()

    const {
      data,
      isSelected,
    }: { data: TaxSelectOptionType; isSelected: boolean } = props

    return (
      <ReactSelectComponents.Option {...props}>
        <div>
          <SubText textEllipsis>{data.name}</SubText>
        </div>
        <div>
          <SubText color={isSelected ? undefined : 'textSecondary'}>
            {formatTaxPercentage(data.percentage)}
          </SubText>
        </div>
        {data.is_default && (
          <div>
            <SubText color={isSelected ? undefined : 'textSecondary'}>
              {intl.formatMessage(messages.defaultTaxLabel)}
            </SubText>
          </div>
        )}
      </ReactSelectComponents.Option>
    )
  },
  SingleValue: (props: SingleValueProps<TaxSelectOptionType, false>) => {
    const formatTaxPercentage = useFormatTaxPercentage()
    const { data }: { data: TaxSelectOptionType } = props

    return (
      <ReactSelectComponents.SingleValue {...props}>
        <div>
          <SubText>{data.name}</SubText>
        </div>
        <div>
          <SubText color='textSecondary'>
            {formatTaxPercentage(data.percentage)}
          </SubText>
        </div>
      </ReactSelectComponents.SingleValue>
    )
  },
}

export interface ITaxSelectProps extends ISelectProps<TaxSelectOptionType> {
  onCreateTax?(): void
}

export const TaxSelect = ({
  onCreateTax = () => undefined,
  ...otherProps
}: ITaxSelectProps) => {
  const intl = useIntl()

  return (
    <Select<TaxSelectOptionType>
      components={components}
      placeholder={intl.formatMessage(messages.selectTaxPlaceholder)}
      getOptionValue={(opt) => opt.id.toString()}
      getOptionLabel={(opt) => opt.name}
      stickies={({ closeMenu }) => {
        return [
          <Select.StickyOption
            key={0}
            onClick={() => {
              onCreateTax()
              closeMenu()
            }}
          >
            {intl.formatMessage(messages.addNewTaxOptionLabel)}
          </Select.StickyOption>,
        ]
      }}
      {...otherProps}
    />
  )
}

TaxSelect.displayName = 'TaxSelect'
